import type { AthenaPrincipal } from "../../../runtime/data/principal.ts";
import {
  AthenaBillingCapabilityError,
  AthenaBillingError,
} from "../../errors.ts";
import type { BillingProviderConfigMap } from "../../providers/types.ts";
import { prepareBillingCommand } from "../../safety/prepare.ts";
import { requireBillingPrincipalUserId } from "../../subject/principal-user.ts";
import type { BillingSqlExecutor } from "../../subject/repository.ts";
import type { BillingCheckout, BillingPrice } from "../../types.ts";
import { billingHalLinkHref } from "../hal-link-href.ts";
import { PROCESS_BILLING_INVOCATION } from "../invocation-authority.ts";
import { executeLocalBillingPaymentCreate } from "../local/execute/payments.ts";
import { executeLocalBillingPriceList } from "../local/execute/price-list.ts";
import type { BillingProviderRegistry } from "../local/providers/registry.ts";
import { resolveBillingCheckoutIntent } from "./checkout-intent.ts";
import { selfDelegatedBillingPrincipal } from "./delegated-principal.ts";
import {
  checkoutAttemptExpiresAtIso,
  getCheckoutSessionByIdempotency,
  insertCheckoutSession,
  isCheckoutSessionResumeExpired,
  updateCheckoutSession,
} from "./enrollment-session.ts";
import { resolveAllowedBillingRedirectUrl } from "./redirect-url.ts";
import { appendBillingReturnToken } from "./return-query.ts";
import { createBillingReturnNonce } from "./return-token.ts";

const OWNED_SUBSCRIPTION_CUSTOMER_SQL = `
SELECT provider_customer_id
FROM billing.billing_subscriptions
WHERE subject_kind = 'user'
  AND subject_id = $1
  AND ownership_status = 'resolved'
  AND provider_customer_id IS NOT NULL
  AND btrim(provider_customer_id) <> ''
ORDER BY ingested_at DESC
LIMIT 1
`;

const OWNED_PAYMENT_CUSTOMER_SQL = `
SELECT provider_customer_id
FROM billing.billing_payments
WHERE subject_kind = 'user'
  AND subject_id = $1
  AND ownership_status = 'resolved'
  AND provider_customer_id IS NOT NULL
  AND btrim(provider_customer_id) <> ''
ORDER BY ingested_at DESC
LIMIT 1
`;

function checkoutUrlFromRaw(raw: unknown): string | null {
  return billingHalLinkHref(raw, "checkout");
}

async function ownedProviderCustomerId(input: {
  principal: AthenaPrincipal;
  sql: BillingSqlExecutor;
}): Promise<string | undefined> {
  const subjectUserId = requireBillingPrincipalUserId(input.principal);
  const fromSubscription = await input.sql.query(
    OWNED_SUBSCRIPTION_CUSTOMER_SQL,
    [subjectUserId]
  );
  const subscriptionCustomer = fromSubscription.rows[0]?.provider_customer_id;
  if (typeof subscriptionCustomer === "string" && subscriptionCustomer.trim()) {
    return subscriptionCustomer;
  }
  const fromPayment = await input.sql.query(OWNED_PAYMENT_CUSTOMER_SQL, [
    subjectUserId,
  ]);
  const paymentCustomer = fromPayment.rows[0]?.provider_customer_id;
  if (typeof paymentCustomer === "string" && paymentCustomer.trim()) {
    return paymentCustomer;
  }
}

export async function createSelfCheckout(input: {
  allowedRedirectOrigins?: readonly string[];
  cancelUrl?: string;
  configuredProviders?: BillingProviderConfigMap;
  idempotencyKey: string;
  principal: AthenaPrincipal;
  priceId: string;
  registry?: BillingProviderRegistry;
  sql: BillingSqlExecutor;
  successUrl?: string;
  testMode?: boolean;
}): Promise<BillingCheckout> {
  const subjectUserId = requireBillingPrincipalUserId(input.principal);
  const command = prepareBillingCommand({
    operation: "self.checkout.create",
    payload: {
      cancelUrl: input.cancelUrl,
      idempotencyKey: input.idempotencyKey,
      priceId: input.priceId,
      successUrl: input.successUrl,
    },
  });
  const priceId = String(command.payload.priceId);
  const idempotencyKey = String(command.payload.idempotencyKey);
  if (input.registry == null) {
    throw new AthenaBillingCapabilityError({
      operation: "self.checkout.create",
      reason: "runtime_unavailable",
    });
  }
  const delegated = selfDelegatedBillingPrincipal(input.principal, [
    "prices.list",
    "payments.create",
  ]);
  const prices = await executeLocalBillingPriceList({
    authority: PROCESS_BILLING_INVOCATION,
    configuredProviders: input.configuredProviders,
    payload: {},
    principal: delegated,
    registry: input.registry,
    testMode: input.testMode,
  });
  const price: BillingPrice | undefined = prices.items.find(
    (item) => item.id === priceId
  );
  if (price == null) {
    throw new AthenaBillingCapabilityError({
      operation: "self.checkout.create",
      reason: "missing_catalog",
    });
  }
  const intent = resolveBillingCheckoutIntent(price);
  if (intent.kind === "recurring") {
    throw new AthenaBillingCapabilityError({
      message:
        "Recurring catalog prices require recurring enrollment and must not execute as one-off payments.",
      operation: "self.checkout.create",
      reason: "unsupported_operation",
    });
  }
  const allowedRedirectOrigins = input.allowedRedirectOrigins ?? [];
  const successUrl = resolveAllowedBillingRedirectUrl({
    allowedOrigins: allowedRedirectOrigins,
    field: "successUrl",
    url: input.successUrl,
  });
  const cancelUrl = resolveAllowedBillingRedirectUrl({
    allowedOrigins: allowedRedirectOrigins,
    field: "cancelUrl",
    url: input.cancelUrl,
  });
  if (successUrl == null && cancelUrl == null) {
    throw new AthenaBillingCapabilityError({
      operation: "self.checkout.create",
      reason: "unsupported_operation",
    });
  }
  const existing = await getCheckoutSessionByIdempotency({
    idempotencyKey,
    sql: input.sql,
    subjectId: subjectUserId,
    subjectKind: "user",
  });
  if (existing) {
    if (isCheckoutSessionResumeExpired(existing)) {
      throw new AthenaBillingError({
        body: { sessionId: existing.id },
        code: "ATHENA_BILLING_INVALID_REQUEST",
        endpoint: "",
        message:
          "Billing checkout session has expired. Retry checkout with a new idempotencyKey.",
        method: "POST",
        status: 400,
      });
    }
    if (
      existing.providerPaymentId != null &&
      existing.providerPaymentId.trim() !== ""
    ) {
      return {
        checkoutUrl: existing.checkoutUrl,
        metadata: {
          paymentId: existing.providerPaymentId,
          priceId: existing.priceId,
          sessionId: existing.id,
        },
        provider: existing.provider,
        providerCheckoutId: existing.providerPaymentId,
        raw: {},
      };
    }
  }
  const customerId = await ownedProviderCustomerId({
    principal: input.principal,
    sql: input.sql,
  });
  const nonce = createBillingReturnNonce();
  const expiresAt = checkoutAttemptExpiresAtIso();
  const correlatedSuccess = successUrl
    ? appendBillingReturnToken(successUrl, nonce.token)
    : undefined;
  const correlatedCancel = cancelUrl
    ? appendBillingReturnToken(cancelUrl, nonce.token)
    : undefined;
  const redirectUrl = correlatedSuccess ?? correlatedCancel;
  if (redirectUrl == null) {
    throw new AthenaBillingCapabilityError({
      operation: "self.checkout.create",
      reason: "unsupported_operation",
    });
  }
  const sessionId = existing?.id ?? crypto.randomUUID();
  const session = existing
    ? await updateCheckoutSession({
        expiresAt,
        id: existing.id,
        returnNonceHash: nonce.hash,
        sql: input.sql,
        status: existing.status,
      })
    : await insertCheckoutSession({
        checkoutUrl: null,
        expiresAt,
        id: sessionId,
        idempotencyKey,
        kind: "one_off",
        metadata: {
          athenaCheckoutSessionId: sessionId,
          cancelUrl: correlatedCancel,
          paymentIdempotencyKey: idempotencyKey,
          priceId: price.id,
          successUrl: correlatedSuccess,
        },
        priceId: price.id,
        provider: "mollie",
        providerCustomerId: customerId ?? null,
        providerPaymentId: null,
        providerSubscriptionId: null,
        returnNonceHash: nonce.hash,
        sql: input.sql,
        status: "first_payment_required",
        subjectId: subjectUserId,
        subjectKind: "user",
      });
  const resolvedSessionId = session.id;
  const payment = await executeLocalBillingPaymentCreate({
    authority: PROCESS_BILLING_INVOCATION,
    configuredProviders: input.configuredProviders,
    payload: {
      amount: price.amount,
      customerId,
      description: `Athena checkout ${price.id}`,
      idempotencyKey,
      metadata: {
        athenaCheckoutSessionId: resolvedSessionId,
        athenaSubjectId: subjectUserId,
        athenaSubjectKind: "user",
        priceId: price.id,
      },
      redirectUrl,
    },
    principal: delegated,
    registry: input.registry,
    testMode: input.testMode,
  });
  const checkoutUrl = checkoutUrlFromRaw(payment.raw);
  await updateCheckoutSession({
    checkoutUrl,
    id: resolvedSessionId,
    providerCustomerId: payment.providerCustomerId ?? customerId ?? null,
    providerPaymentId: payment.providerPaymentId,
    sql: input.sql,
    status: "first_payment_required",
  });
  return {
    checkoutUrl,
    metadata: {
      paymentId: payment.providerPaymentId,
      priceId: price.id,
      sessionId: resolvedSessionId,
    },
    provider: payment.provider,
    providerCheckoutId: payment.providerPaymentId,
    raw: payment.raw,
  };
}
